import { APP_URL } from "@/src/core/config/app";
import { ApiError } from "@/src/core/api/types";
import type { IHostProfile } from "./types";

const fullName = (host: IHostProfile) =>
  [host.first_name, host.last_name].filter(Boolean).join(" ");

export const hostService = {
  fullName,

  /** `host_id` from the route; null unless it is a positive integer. */
  parseHostId(raw: unknown): number | null {
    if (typeof raw !== "string" || !/^\d+$/.test(raw)) return null;
    const id = Number(raw);
    return id > 0 ? id : null;
  },

  /** A missing host is "not found", not a failure. Laravel answers a missing host with 500, not 404. */
  failure(error: unknown): unknown {
    return error instanceof ApiError && [404, 500].includes(error.status)
      ? null
      : error;
  },

  /** Completed stays, from whichever key the payload carries. */
  completedStays(host: IHostProfile): number {
    const record = host as unknown as Record<string, unknown>;
    const value = record["total_booking"] ?? record["totalBookings"];
    return typeof value === "number" ? value : 0;
  },

  avatarUrl(host: IHostProfile): string | null {
    if (typeof host.image?.url === "string") return host.image.url;
    const avatar = (host as unknown as Record<string, unknown>)["avatar"];
    return typeof avatar === "string" ? avatar : null;
  },

  /** Title, description and canonical for /host-details/{id}. */
  buildSeo(host: IHostProfile | null, hostId: number | null) {
    const id = host?.id ?? hostId;
    return {
      title: host
        ? `${fullName(host)} - Profile | Travela`
        : "Host Profile | Travela",
      description: host
        ? `Hello, I'm ${fullName(host)} • Professional Host • ${hostService.completedStays(host)} completed stays`
        : "Discover amazing properties from professional hosts on Travela.",
      canonical: id
        ? `${APP_URL}/host-details/${id}`
        : `${APP_URL}/host-details`,
    };
  },

  /** Split listings into rows of `size` for the sliders. */
  chunk<T>(items: T[], size: number): T[][] {
    const rows: T[][] = [];
    for (let i = 0; i < items.length; i += size)
      rows.push(items.slice(i, i + size));
    return rows;
  },
};
